import { useCallback, useEffect, useReducer, useRef } from 'react'
import {
  CommentHubAPIError,
  type CommentHubAddCatalogDeviceBody,
  type CommentHubAddTypedDeviceBody,
  type CommentHubCatalogDevice,
  type CommentHubDevice,
  type CommentHubDevicePatch,
} from 'shared-code/commenthub-api'
import { logError } from 'shared-code/log'
import { createContainer } from 'shared-components/unstated'
import { AuthContext } from 'shared-components/contexts/AuthContext'
import { CommentHubAPIContext } from 'shared-components/contexts/CommentHubAPIContext'
import { NotificationsContext } from 'shared-components/contexts/NotificationsContext'
import { profileErrorMessage } from 'shared-components/lib/profile-name'
import { devicesReducer, EMPTY_DEVICES, type MyDevicesData } from './state'

/** Fired on `window` after every device write, for any surface outside this provider. */
export const DEVICES_CHANGED_EVENT = 'aa:devices-changed'

export interface DeviceDates {
  startedAt?: string
  endedAt?: string
}

/** How the reader chose a catalog phone: confirmed from Client Hints (with its code) or picked. */
export interface DeviceChoice {
  source: CommentHubAddCatalogDeviceBody['source']
  modelCode?: string
}

export interface MyDevices extends MyDevicesData {
  reload: () => void
  /** Closes the current phone and opens this catalog phone (D8: only a `catalogId` is sent).
   *  Resolves true once commentHub stored it. */
  add: (phone: CommentHubCatalogDevice, choice: DeviceChoice) => Promise<boolean>
  /** Closes the current phone and opens a model the reader typed (not in the catalog; brand
   *  stays empty). Resolves true once stored, else the message to show inline under the field:
   *  commentHub's own for `INVALID_DEVICE`, a generic one otherwise. Never toasts an error. */
  addTyped: (model: string) => Promise<true | string>
  setShow: (id: number, show: boolean) => Promise<boolean>
  /** A previous phone's months (never clears endedAt — commentHub refuses null). `invalid-dates`
   *  = commentHub's INVALID_DATES, for the form's inline line; other failures toast. */
  setDates: (id: number, dates: DeviceDates) => Promise<'saved' | 'invalid-dates' | 'failed'>
  /** "Remove": the current phone joins history, switched off. */
  removeCurrent: (id: number) => Promise<boolean>
  /** "Forget": the row leaves the history and every comment. */
  forget: (id: number) => Promise<boolean>
  /** The once-ever "Which phone do you use?" notification (after Skip). Never toasts. */
  prompt: () => Promise<void>
}

const noop = (): Promise<boolean> => Promise.resolve(false)
const noopDates = (): Promise<'failed'> => Promise.resolve('failed')

export const TYPED_SAVE_FAILED = 'Could not save your phone. Please try again.'

/** No provider (a spec rendering Header alone): known nothing, does nothing. */
export const INERT_DEVICES: MyDevices = {
  ...EMPTY_DEVICES,
  reload: () => undefined,
  add: noop,
  addTyped: () => Promise.resolve(TYPED_SAVE_FAILED),
  setShow: noop,
  setDates: noopDates,
  removeCurrent: noop,
  forget: noop,
  prompt: () => Promise.resolve(),
}

function announce(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(DEVICES_CHANGED_EVENT))
  }
}

/**
 * The signed-in reader's phones on Android Authority — ONE instance, mounted in Layout, read by
 * the header row, the device step and Profile › Device (`useMyDevices`). `site` is the commentHub
 * apex (`CommentHubAPIContext.site`, the value the tray sends as `?site=`); unset → `unavailable`
 * and nothing is fetched.
 */
function useMyDevicesState(): MyDevices {
  const auth = AuthContext.useOptionalContainer()
  const hub = CommentHubAPIContext.useOptionalContainer()
  const toasts = NotificationsContext.useOptionalContainer()
  const api = hub?.api ?? null
  const site = hub?.site ?? null
  const withAuth = auth?.withAuth
  const [state, dispatch] = useReducer(devicesReducer, EMPTY_DEVICES)
  const stateRef = useRef(state)
  stateRef.current = state
  const generation = useRef(0)

  const toastError = useCallback(
    (e: unknown, fallback: string): void => {
      toasts?.enqueue({ msg: profileErrorMessage(e, fallback), variant: 'error', duration: 5000 })
    },
    [toasts]
  )

  const load = useCallback((): void => {
    if (!api || !site || !withAuth) {
      return
    }
    const mine = ++generation.current
    if (stateRef.current.status !== 'ready') {
      dispatch({ type: 'status', status: 'loading' })
    }
    void withAuth(t => api.getDevices(t, site))
      .then(list => {
        if (mine === generation.current) {
          dispatch({ type: 'loaded', list })
        }
      })
      .catch(e => {
        if (mine === generation.current) {
          dispatch({ type: 'status', status: 'error' })
        }
        logError('Failed to load devices', e)
      })
  }, [api, site, withAuth])

  // Keyed on the TOKEN, not the authState object (use-commenthub-profile: a new authState
  // identity per profile adopt would refetch forever).
  const token = auth?.authState?.token ?? null
  useEffect(() => {
    generation.current++
    if (!token) {
      dispatch({ type: 'status', status: 'idle' })
      return
    }
    if (!api || !site) {
      dispatch({ type: 'status', status: 'unavailable' })
      return
    }
    load()
    // `load` changes with withAuth's identity on every provider render; the token is the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, api, site])

  const add = useCallback(
    async (phone: CommentHubCatalogDevice, choice: DeviceChoice): Promise<boolean> => {
      if (!api || !site || !withAuth) {
        return false
      }
      const body: CommentHubAddCatalogDeviceBody = {
        site,
        catalogId: phone.id,
        source: choice.source,
      }
      if (choice.modelCode) {
        body.modelCode = choice.modelCode
      }
      try {
        const list = await withAuth(t => api.addDevice(t, body))
        generation.current++
        dispatch({ type: 'loaded', list })
        toasts?.enqueue({ msg: `${phone.name} saved`, variant: 'success', duration: 3000 })
        announce()
        return true
      } catch (e) {
        toastError(e, 'Could not save your phone. Please try again.')
        logError('Failed to add device', e)
        return false
      }
    },
    [api, site, withAuth, toasts, toastError]
  )

  const addTyped = useCallback(
    async (typed: string): Promise<true | string> => {
      if (!api || !site || !withAuth) {
        return TYPED_SAVE_FAILED
      }
      const model = typed.trim()
      const body: CommentHubAddTypedDeviceBody = { site, source: 'typed', model }
      try {
        const list = await withAuth(t => api.addDevice(t, body))
        generation.current++
        dispatch({ type: 'loaded', list })
        toasts?.enqueue({ msg: `${model} saved`, variant: 'success', duration: 3000 })
        announce()
        return true
      } catch (e) {
        if (e instanceof CommentHubAPIError && e.code === 'INVALID_DEVICE' && e.message) {
          return e.message
        }
        logError('Failed to add typed device', e)
        return profileErrorMessage(e, TYPED_SAVE_FAILED)
      }
    },
    [api, site, withAuth, toasts]
  )

  const patch = useCallback(
    async (
      id: number,
      body: CommentHubDevicePatch,
      optimistic: Partial<CommentHubDevice>,
      fallback: string,
      quietCodes: readonly string[] = []
    ): Promise<true | string | false> => {
      if (!api || !withAuth) {
        return false
      }
      const before = [stateRef.current.current, ...stateRef.current.previous].find(
        d => d?.id === id
      )
      if (!before) {
        return false
      }
      dispatch({ type: 'patch', id, patch: optimistic })
      try {
        const row = await withAuth(t => api.updateDevice(t, id, body))
        dispatch({ type: 'patch', id, patch: row })
        announce()
        return true
      } catch (e) {
        dispatch({ type: 'patch', id, patch: before })
        const code = e instanceof CommentHubAPIError ? e.code : null
        if (code && quietCodes.includes(code)) {
          return code
        }
        toastError(e, fallback)
        logError('Failed to update device', e)
        if (e instanceof CommentHubAPIError && e.status === 404) {
          // Not ours any more (forgotten elsewhere): re-read the list.
          load()
        }
        return false
      }
    },
    [api, withAuth, toastError, load]
  )

  const setShow = useCallback(
    async (id: number, show: boolean): Promise<boolean> =>
      (await patch(
        id,
        { showOnComments: show },
        { showOnComments: show },
        'Could not change that. Please try again.'
      )) === true,
    [patch]
  )

  const setDates = useCallback(
    async (id: number, dates: DeviceDates): Promise<'saved' | 'invalid-dates' | 'failed'> => {
      const result = await patch(
        id,
        dates,
        dates,
        'Could not save those dates. Please try again.',
        ['INVALID_DATES']
      )
      if (result === true) {
        return 'saved'
      }
      return result === 'INVALID_DATES' ? 'invalid-dates' : 'failed'
    },
    [patch]
  )

  const removeCurrent = useCallback(
    async (id: number): Promise<boolean> => {
      if (!api || !withAuth) {
        return false
      }
      const snapshot = stateRef.current
      const endedAt = new Date().toISOString()
      dispatch({ type: 'removeCurrent', id, endedAt })
      try {
        await withAuth(t => api.updateDevice(t, id, { endedAt, showOnComments: false }))
        announce()
        return true
      } catch (e) {
        if (snapshot.status === 'ready') {
          dispatch({ type: 'loaded', list: snapshot })
        }
        toastError(e, 'Could not remove your phone. Please try again.')
        logError('Failed to remove device', e)
        return false
      }
    },
    [api, withAuth, toastError]
  )

  const forget = useCallback(
    async (id: number): Promise<boolean> => {
      if (!api || !withAuth) {
        return false
      }
      const snapshot = stateRef.current
      dispatch({ type: 'forget', id })
      try {
        await withAuth(t => api.deleteDevice(t, id))
        announce()
        return true
      } catch (e) {
        if (snapshot.status === 'ready') {
          dispatch({ type: 'loaded', list: snapshot })
        }
        toastError(e, 'Could not forget that phone. Please try again.')
        logError('Failed to forget device', e)
        return false
      }
    },
    [api, withAuth, toastError]
  )

  const prompt = useCallback(async (): Promise<void> => {
    if (!api || !site || !withAuth) {
      return
    }
    try {
      await withAuth(t => api.promptDevice(t, site))
    } catch (e) {
      logError('Failed to request the device prompt', e)
    }
  }, [api, site, withAuth])

  return { ...state, reload: load, add, addTyped, setShow, setDates, removeCurrent, forget, prompt }
}

export const MyDevicesContext = createContainer<MyDevices>(useMyDevicesState)

/** The reader's phones (one provider in Layout); inert outside it. */
export function useMyDevices(): MyDevices {
  return MyDevicesContext.useOptionalContainer() ?? INERT_DEVICES
}
